import { Paginated } from "@feathersjs/feathers/lib";
import { DataType, IStep, Services, IVideo, VideoChapter } from "types";
import { makeObservable, observable, action, computed, runInAction } from "mobx";
import client from "../feathers";
import { Content } from ".";


export class Video extends Content<IVideo> implements IVideo {

  course_id: number = null;
  title:     string = null;
  step_id:   number = null;
  vimeo_id:  number = null;
  url?:      string = null;
  chapters:  VideoChapter[] = [];
  subtitles: any[] = [];

  constructor(data: IVideo) {
    // The super constructr takes care of references to the service, validation rules
    // and intialized the base members with mobx decorators
    super(DataType.Video, data);

    // Since Mobx cannot track derived class objects automatically,
    // the decorators have to be provided manually
    makeObservable(this, {
      vimeo_id:  observable,
      title:     observable,
      chapters:  observable,
      course_id: observable,
      step_id:   observable,
      url:       observable,
      subtitles: observable,

      getVimeoId:  computed,
      getChapters: computed,

      addChapter:    action,
      removeChapter: action,
      saveChapters:  action,
    });

    // Initialize data
    // While the super constructor will call this once,
    // the default values given to members will overwrite this so it has to be called again here.
    this.fromData(data);
  }
  /*******************************************************************************************************************************************
   * Database Access
   ********************************************************************************************************************************************/

  public saveChapters = async() => {
    await this.set('chapters', this.chapters, true);
  }

  public getVimeoVideo = async():Promise<string|null> => {
    try {
      if(!this.getVimeoId) return;
      const response = await client.service(Services.Video).getVimeoURL({vimeo_id: this.getVimeoId});
      const url = response.video_url;
      const subtitles = response.subtitles;
      console.log(response);
      runInAction(() => this.url = url);
      runInAction(() => this.subtitles = subtitles);
    } catch (error) {
    console.error("Failed to get Vimeo Video", error);
    return null;
    }
  }

  /*******************************************************************************************************************************************
   * Overwrites
   ********************************************************************************************************************************************/

  public static get = async (id?: number): Promise<Video | Video[]> => {
    try {
      if (id) {
        return Video.create(await client.service(Services.Video).get(id));
      } else {
        const videos: Paginated<IVideo> = await client.service(Services.Video).find();
        if (!videos.total) return [];
        return videos.data.map(v => Video.create(v));
      }
    } catch (err) {
      console.error("Failed to get Video for step", err);
      return null;
    }
  }

  public static create(data: IVideo): Video {
    return new Video(data);
  }

  protected fromData(data: IVideo): void {
    super.fromData(data);
    this.vimeo_id  = data.vimeo_id && data.vimeo_id;
    this.title     = data.title && data.title;
    this.course_id = data.course_id && Number(data.course_id);
    this.step_id   = data.step_id && Number(data.step_id);
    this.chapters  = (data.chapters && data.chapters.length && data.chapters.map(c => c)) || [];
  }

  public toData(): any {
    return {
      ...super.toData(),
      course_id: this.course_id || null,
      step_id:   this.step_id   || null,
      vimeo_id:  this.vimeo_id  || null,
      title:     this.title     || '',
      chapters:  JSON.stringify(this.chapters) || {},
    };
  }

  public toPatchData(patch: Partial<IStep>): any {
    return {
      ...super.toPatchData(patch),
      course_id: "course_id" in patch ? this.course_id:          undefined,
      step_id:   "step_id" in patch ? this.step_id:              undefined,
      vimeo_id:  "vimeo_id" in patch ? this.vimeo_id:            undefined,
      chapters:  "chapters" in patch ? JSON.stringify(this.chapters): {},
      title:     "title" in patch ? this.title:                  '',
    };
  }

  /*******************************************************************************************************************************************
   * Getter
   ********************************************************************************************************************************************/

  public get canEdit(): boolean {
    return true;
  }

  public get getVimeoId(): number {
    return this.vimeo_id || null;
  }

  public get getChapters(): VideoChapter[] {
    return (this.chapters || []).slice()
  }

  /*******************************************************************************************************************************************
   * Setter
   ********************************************************************************************************************************************/

  /*******************************************************************************************************************************************
   * Actions
   ********************************************************************************************************************************************/
  
  public addChapter = async (chapter: VideoChapter): Promise<boolean> => {
    return await this.set("chapters", [...this.chapters, chapter], true);
  }

  public removeChapter = async (chapter: VideoChapter): Promise<boolean> => {
    return await this.set("chapters", this.chapters.filter( c => c.id !== chapter.id), true);
  }
}
